import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import BotForm from './BotForm';
import OpenAI from 'openai';

const BotListPane = ({ user, onBotSelect, selectedNeobot }) => {
  const [neobots, setNeobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingNeobot, setEditingNeobot] = useState(null);
  const [newNeobot, setNewNeobot] = useState({ name: '', description: '', avatarPrompt: '', avatarUrl: '' });
  
  // Loading states for AI generation
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isGeneratingAvatarPrompt, setIsGeneratingAvatarPrompt] = useState(false);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingAvatarPrompt, setIsEditingAvatarPrompt] = useState(false);
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);

  const openai = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true
  });

  // Load bots from Supabase
  useEffect(() => {
    if (user) {
      loadBots();
    }
  }, [user]);

  const loadBots = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('neobots')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map database fields to component field names
      const mappedBots = (data || []).map(bot => ({
        id: bot.id,
        name: bot.name,
        description: bot.description,
        avatarPrompt: bot.avatar_prompt || '',
        avatarUrl: bot.avatar_url || '',
        systemPrompt: bot.system_prompt,
        createdAt: bot.created_at
      }));

      setNeobots(mappedBots);
    } catch (error) {
      console.error('Error loading bots:', error);
      alert('Failed to load bots. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const generateDescription = async (isEdit = false) => {
    const botData = isEdit ? editingNeobot : newNeobot;
    if (!botData.name.trim()) {
      alert('Please enter a bot name first!');
      return;
    }

    if (isEdit) {
      setIsEditingDescription(true);
    } else {
      setIsGeneratingDescription(true);
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are a helpful assistant that creates concise, engaging descriptions for AI chatbots. Keep descriptions under 100 characters and make them fun and descriptive."
          },
          {
            role: "user",
            content: `Create a short, engaging description for a chatbot named "${botData.name}". The description should explain what this bot does or what personality it has. Keep it under 100 characters.`
          }
        ],
        max_tokens: 100,
      });

      const generatedDescription = completion.choices[0].message.content.trim();

      if (isEdit) {
        setEditingNeobot(prev => ({ ...prev, description: generatedDescription }));
      } else {
        setNewNeobot(prev => ({ ...prev, description: generatedDescription }));
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Failed to generate description. Please try again.');
    } finally {
      if (isEdit) {
        setIsEditingDescription(false);
      } else {
        setIsGeneratingDescription(false);
      }
    }
  };

  const generateAvatarPrompt = async (isEdit = false) => {
    const botData = isEdit ? editingNeobot : newNeobot;
    if (!botData.name.trim() || !botData.description.trim()) {
      alert('Please enter both a bot name and description first!');
      return;
    }

    if (isEdit) {
      setIsEditingAvatarPrompt(true);
    } else {
      setIsGeneratingAvatarPrompt(true);
    }

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are a helpful assistant that creates image generation prompts for AI avatars. Create detailed, artistic prompts that would generate a good avatar image."
          },
          {
            role: "user",
            content: `Create a detailed image generation prompt for an avatar of a chatbot named "${botData.name}" with the description: "${botData.description}". The prompt should be suitable for DALL-E or similar AI image generators. Make it detailed and artistic, describing the visual style, colors, and character appearance.`
          }
        ],
        max_tokens: 200,
      });

      const generatedPrompt = completion.choices[0].message.content.trim();

      if (isEdit) {
        setEditingNeobot(prev => ({ ...prev, avatarPrompt: generatedPrompt }));
      } else {
        setNewNeobot(prev => ({ ...prev, avatarPrompt: generatedPrompt }));
      }
    } catch (error) {
      console.error('Error generating avatar prompt:', error);
      alert('Failed to generate avatar prompt. Please try again.');
    } finally {
      if (isEdit) {
        setIsEditingAvatarPrompt(false);
      } else {
        setIsGeneratingAvatarPrompt(false);
      }
    }
  };

  const generateAvatar = async (isEdit = false) => {
    const botData = isEdit ? editingNeobot : newNeobot;
    if (!botData.avatarPrompt.trim()) {
      alert('Please enter an avatar prompt first!');
      return;
    }

    if (isEdit) {
      setIsEditingAvatar(true);
    } else {
      setIsGeneratingAvatar(true);
    }

    try {
      const response = await openai.images.generate({
        model: "dall-e-3",
        prompt: botData.avatarPrompt,
        size: "1024x1024",
        quality: "standard",
        n: 1,
      });

      const avatarUrl = response.data[0].url;

      if (isEdit) {
        setEditingNeobot(prev => ({ ...prev, avatarUrl }));
      } else {
        setNewNeobot(prev => ({ ...prev, avatarUrl }));
      }
    } catch (error) {
      console.error('Error generating avatar:', error);
      alert('Failed to generate avatar. Please try again.');
    } finally {
      if (isEdit) {
        setIsEditingAvatar(false);
      } else {
        setIsGeneratingAvatar(false);
      }
    }
  };

  const createNeobot = async () => {
    if (!newNeobot.name.trim() || !newNeobot.description.trim() || !user) return;

    try {
      const neobotData = {
        user_id: user.id,
        name: newNeobot.name,
        description: newNeobot.description,
        avatar_prompt: newNeobot.avatarPrompt,
        avatar_url: newNeobot.avatarUrl,
        system_prompt: `You are ${newNeobot.name}. ${newNeobot.description}. Always respond in character as ${newNeobot.name}.`,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('neobots')
        .insert([neobotData])
        .select()
        .single();

      if (error) throw error;

      const neobot = {
        id: data.id,
        name: data.name,
        description: data.description,
        avatarPrompt: data.avatar_prompt,
        avatarUrl: data.avatar_url,
        systemPrompt: data.system_prompt,
        createdAt: data.created_at
      };

      setNeobots(prev => [neobot, ...prev]);
      setNewNeobot({ name: '', description: '', avatarPrompt: '', avatarUrl: '' });
      setShowCreateForm(false);

      // Auto-select the newly created bot
      onBotSelect(neobot);
    } catch (error) {
      console.error('Error creating bot:', error);
      alert('Failed to create bot. Please try again.');
    }
  };

  const editNeobot = (neobot) => {
    setEditingNeobot({
      id: neobot.id,
      name: neobot.name,
      description: neobot.description,
      avatarPrompt: neobot.avatarPrompt || '',
      avatarUrl: neobot.avatarUrl || '',
      systemPrompt: neobot.systemPrompt,
      createdAt: neobot.createdAt
    });
    setShowEditForm(true);
  };

  const saveEditedNeobot = async () => {
    if (!editingNeobot.name.trim() || !editingNeobot.description.trim() || !user) return;

    try {
      const updatedData = {
        name: editingNeobot.name,
        description: editingNeobot.description,
        avatar_prompt: editingNeobot.avatarPrompt,
        avatar_url: editingNeobot.avatarUrl,
        system_prompt: `You are ${editingNeobot.name}. ${editingNeobot.description}. Always respond in character as ${editingNeobot.name}.`,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('neobots')
        .update(updatedData)
        .eq('id', editingNeobot.id)
        .eq('user_id', user.id);

      if (error) throw error;

      const updatedNeobot = {
        ...editingNeobot,
        systemPrompt: updatedData.system_prompt
      };

      setNeobots(prev => prev.map(bot =>
        bot.id === editingNeobot.id ? updatedNeobot : bot
      ));

      // Update selected bot if it's the one being edited
      if (selectedNeobot && selectedNeobot.id === editingNeobot.id) {
        onBotSelect(updatedNeobot);
      }

      setEditingNeobot(null);
      setShowEditForm(false);
    } catch (error) {
      console.error('Error updating bot:', error);
      alert('Failed to update bot. Please try again.');
    }
  };

  const deleteNeobot = async (id) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('neobots')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setNeobots(prev => prev.filter(bot => bot.id !== id));
      if (selectedNeobot && selectedNeobot.id === id) {
        onBotSelect(null);
      }
    } catch (error) {
      console.error('Error deleting bot:', error);
      alert('Failed to delete bot. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="aim-chat-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div className="aim-system-message">
          Loading your bots...
        </div>
      </div>
    );
  }

  return (
    <div className="aim-chat-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="aim-chat-header">
        <span className="aim-status-indicator aim-status-online"></span>
        NeoBots
      </div>

      <div className="aim-button-group">
        <button 
          className="aim-button"
          onClick={() => setShowCreateForm(true)}
        >
          + New Bot
        </button>
      </div>
      
      <div style={{ flex: 1, overflow: 'auto', marginTop: '10px' }}>
        {neobots.length === 0 && (
          <div className="aim-system-message" style={{ margin: '10px 0' }}>
            No bots created yet. Create your first bot!
          </div>
        )}

        {neobots.map(neobot => (
          <div 
            key={neobot.id}
            className={`aim-user-item ${selectedNeobot?.id === neobot.id ? 'aim-status-online' : ''}`}
            onClick={() => onBotSelect(neobot)}
          style={{
              cursor: 'pointer',
              backgroundColor: selectedNeobot?.id === neobot.id ? 'rgba(0, 255, 0, 0.1)' : 'transparent'
            }}
          >
            <span className="aim-status-indicator aim-status-online"></span>
            {neobot.avatarUrl && (
              <img
                src={neobot.avatarUrl}
                alt={neobot.name}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  marginRight: '8px',
                  border: '1px solid #ccc'
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{neobot.name}</div>
              <div style={{ fontSize: '10px', color: '#666' }}>{neobot.description}</div>
            </div>
            <div style={{ display: 'flex', gap: '2px' }}>
              <button
                className="aim-button"
                style={{ padding: '2px 6px', fontSize: '10px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  editNeobot(neobot);
                }}
                title="Edit bot"
              >
                ✏️
              </button>
              <button 
                className="aim-button"
                style={{ padding: '2px 6px', fontSize: '10px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNeobot(neobot.id);
                }}
                title="Delete bot"
              >
                ×
              </button>
            </div>
        </div>
      ))}
      </div>

      {showCreateForm && (
        <BotForm
          botData={newNeobot}
          setBotData={setNewNeobot}
          onSubmit={createNeobot}
          onCancel={() => setShowCreateForm(false)}
          title="Create New NeoBot"
          isGeneratingDesc={isGeneratingDescription}
          isGeneratingPrompt={isGeneratingAvatarPrompt}
          isGeneratingAvatar={isGeneratingAvatar}
          onGenerateDesc={() => generateDescription(false)}
          onGeneratePrompt={() => generateAvatarPrompt(false)}
          onGenerateAvatar={() => generateAvatar(false)}
        />
      )}

      {showEditForm && editingNeobot && (
        <BotForm
          botData={editingNeobot}
          setBotData={setEditingNeobot}
          onSubmit={saveEditedNeobot}
          onCancel={() => {
            setEditingNeobot(null);
            setShowEditForm(false);
          }}
          title="Edit NeoBot"
          isGeneratingDesc={isEditingDescription}
          isGeneratingPrompt={isEditingAvatarPrompt}
          isGeneratingAvatar={isEditingAvatar}
          onGenerateDesc={() => generateDescription(true)}
          onGeneratePrompt={() => generateAvatarPrompt(true)}
          onGenerateAvatar={() => generateAvatar(true)}
        />
      )}
    </div>
  );
};

export default BotListPane; 